import { devLog } from '../utils/logger';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { Loader2, CheckCircle, Clock, ArrowLeft, XCircle } from 'lucide-react';
import { SessionInfoPanel } from '../components/session/SessionInfoPanel';
import { ChatPanel } from '../components/session/ChatPanel';
import { MockVideoRoom, RealVideoRoom } from '../components/session/VideoRoom';
import { SessionTypeMessage } from '../components/session/SessionHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

export default function SessionPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [ws, setWs] = useState(null);
  const [connected, setConnected] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const typingTimeoutRef = useRef(null);

  const paymentStatus = searchParams.get('payment');

  const fetchSession = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sessions/${sessionId}`, {
        headers: getAuthHeaders(), withCredentials: true
      });
      setSession(data);
      if (data.messages) setMessages(data.messages);
    } catch (_) {
      setError('Sessão não encontrada');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const checkPaymentStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sessions/${sessionId}/payment-status`, {
        headers: getAuthHeaders(), withCredentials: true
      });
      if (data.payment_status === 'paid') fetchSession();
    } catch (err) {
      devLog.error(err);
    }
  }, [sessionId, fetchSession]);

  useEffect(() => {
    if (sessionId) fetchSession();
  }, [sessionId, fetchSession]);

  useEffect(() => {
    if (session && session.payment_status !== 'paid') {
      const interval = setInterval(checkPaymentStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [session?.payment_status, checkPaymentStatus]);

  const connectWebSocket = useCallback(() => {
    const wsToken = sessionStorage.getItem('auth_token') || localStorage.getItem('token');
    const websocket = new WebSocket(`${WS_URL}/ws/chat/${sessionId}?token=${wsToken}`);
    
    websocket.onopen = () => setConnected(true);
    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'message') {
        setMessages(prev => [...prev, {
          message_id: data.message_id, sender_name: data.sender_name,
          is_professional: data.is_professional, content: data.content, created_at: data.timestamp
        }]);
      } else if (data.type === 'typing') {
        setTypingUser(data.is_typing ? data.user_name : null);
      }
    };
    websocket.onclose = () => setConnected(false);
    setWs(websocket);
  }, [sessionId]);

  useEffect(() => {
    if (session?.chat_enabled && session?.status === 'in_progress' && user) {
      connectWebSocket();
    }
    return () => { if (ws) ws.close(); };
    // ws excluded to avoid reconnect loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status, session?.chat_enabled, user, connectWebSocket]);

  const sendMessage = useCallback(() => {
    if (!newMessage.trim() || !ws || !connected) return;
    ws.send(JSON.stringify({ type: 'message', content: newMessage.trim() }));
    setNewMessage('');
  }, [newMessage, ws, connected]);

  const handleTyping = useCallback((e) => {
    setNewMessage(e.target.value);
    if (ws && connected) {
      ws.send(JSON.stringify({ type: 'typing', is_typing: true }));
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        ws.send(JSON.stringify({ type: 'typing', is_typing: false }));
      }, 1000);
    }
  }, [ws, connected]);

  const startSession = async () => {
    try {
      const { data } = await axios.put(`${API_URL}/api/sessions/${sessionId}/start`, {}, {
        headers: getAuthHeaders(), withCredentials: true
      });
      setSession(data);
    } catch (err) { devLog.error(err); }
  };

  const completeSession = async () => {
    try {
      await axios.put(`${API_URL}/api/sessions/${sessionId}/complete`, {}, {
        headers: getAuthHeaders(), withCredentials: true
      });
      fetchSession();
    } catch (err) { devLog.error(err); }
  };

  const isProfessional = user && session && user.id === session.professional_id;
  const isUser = user && session && user.id === session.user_id;
  const isMockVideo = session?.video_room_url?.startsWith('mock://');

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#4A5D4E] animate-spin" />
        </main>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center">
          <XCircle className="w-16 h-16 text-[#B85C5C] mb-4" />
          <p className="text-[#B85C5C] mb-4">{error || 'Sessão não encontrada'}</p>
          <Link to="/dashboard" className="btn-secondary">Voltar ao Dashboard</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />
      <main className="flex-1 py-8" data-testid="session-page">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#63635E] hover:text-[#292927] mb-6">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Link>

          {paymentStatus === 'success' && session.payment_status === 'paid' && (
            <div className="mb-6 p-4 rounded-xl bg-[#6B8E7C]/10 text-[#6B8E7C] flex items-center gap-3" data-testid="payment-success-banner">
              <CheckCircle className="w-5 h-5" /> Pagamento confirmado! Sua sessão foi agendada.
            </div>
          )}

          {session.payment_status !== 'paid' && (
            <div className="mb-6 p-4 rounded-xl bg-[#D4A373]/10 text-[#D4A373] flex items-center gap-3">
              <Clock className="w-5 h-5" /> Aguardando confirmação do pagamento...
              <Loader2 className="w-4 h-4 animate-spin ml-2" />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-6">
              <SessionInfoPanel
                session={session}
                isProfessional={isProfessional}
                onStart={startSession}
                onComplete={completeSession}
              />
            </div>

            <div className="lg:col-span-2 space-y-6">
              {session.session_type === 'video' && session.status === 'in_progress' && isMockVideo && (
                <MockVideoRoom session={session} onEnd={isProfessional ? completeSession : null} />
              )}

              {session.session_type === 'video' && session.video_room_url && !isMockVideo && session.status === 'in_progress' && (
                <RealVideoRoom session={session} />
              )}

              {session.chat_enabled ? (
                <ChatPanel
                  session={session}
                  messages={messages}
                  newMessage={newMessage}
                  connected={connected}
                  typingUser={typingUser}
                  onTyping={handleTyping}
                  onSend={sendMessage}
                  isProfessional={isProfessional}
                  isUser={isUser}
                />
              ) : (
                <div className="card-theraflow p-8 text-center">
                  <SessionTypeMessage session={session} />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
